/**
 * Metal Type click → scroll gallery so that metal's first image starts at gallery top.
 */
const ProductMetalGallery = {
  PHRASES: ['rose gold', 'yellow gold', 'white gold', 'platinum'],
  lastMetalClickAt: 0,

  isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  },

  getGallery(productInfo) {
    return productInfo.querySelector('media-gallery');
  },

  getSlides(productInfo) {
    return this.getGallery(productInfo)?.querySelectorAll('[id^="GalleryViewer"] [data-media-id]') || [];
  },

  getSelectedMetal(productInfo) {
    const fieldset = productInfo.querySelector('variant-selects .product-form__input--metal-swatch');
    return fieldset?.querySelector('input[type="radio"]:checked')?.value?.trim() || '';
  },

  getMetalFromVariant(variant) {
    if (!variant) return '';

    const options =
      variant.options?.filter(Boolean) ||
      [variant.option1, variant.option2, variant.option3].filter(Boolean);

    return (
      options.find((option) => {
        const lower = String(option).toLowerCase();
        return lower.includes('gold') || lower.includes('platinum') || lower === 'pt';
      }) || ''
    );
  },

  getMetalValues(productInfo, variant, metalValue = '') {
    return [...new Set([metalValue, this.getSelectedMetal(productInfo), this.getMetalFromVariant(variant)].filter(Boolean))];
  },

  normalizePhrase(metalValue) {
    const value = metalValue.toLowerCase().trim();
    const match = this.PHRASES.find((phrase) => value.includes(phrase));
    if (match) return match;
    if (value.includes('rose')) return 'rose gold';
    if (value.includes('yellow')) return 'yellow gold';
    if (value.includes('white')) return 'white gold';
    if (value.includes('platinum') || /\bpt\b/.test(value)) return 'platinum';
    return value.replace(/^\d+k\s*/i, '').trim();
  },

  slideText(slide) {
    const parts = [];
    if (slide.dataset.mediaAlt) parts.push(slide.dataset.mediaAlt.toLowerCase());

    const img = slide.querySelector('img');
    if (img?.alt) parts.push(img.alt.toLowerCase());
    if (img?.currentSrc || img?.src) parts.push((img.currentSrc || img.src).toLowerCase());

    return parts.join(' ');
  },

  slideMatchesMetal(slide, metalValue) {
    if (!metalValue) return false;

    const text = this.slideText(slide);
    if (!text) return false;

    const normalized = metalValue.toLowerCase();
    const phrase = this.normalizePhrase(metalValue);
    const withoutKarat = normalized.replace(/^\d+k\s*/i, '').trim();

    const hasColor =
      text.includes('white gold') ||
      text.includes('yellow gold') ||
      text.includes('rose gold') ||
      text.includes('platinum');

    if (!hasColor) return false;

    if (phrase === 'white gold' && !text.includes('white gold')) return false;
    if (phrase === 'yellow gold' && !text.includes('yellow gold')) return false;
    if (phrase === 'rose gold' && !text.includes('rose gold')) return false;
    if (phrase === 'platinum' && !text.includes('platinum') && !/\bpt\b/.test(text)) return false;

    if (text.includes(normalized)) return true;
    if (withoutKarat && text.includes(withoutKarat)) return true;
    if (phrase && text.includes(phrase)) return true;

    const dashed = phrase.replace(/\s+/g, '-');
    const compact = phrase.replace(/\s+/g, '');

    return text.includes(dashed) || text.includes(compact);
  },

  findSlideByMetal(productInfo, variant, metalValue = '') {
    const metalValues = this.getMetalValues(productInfo, variant, metalValue);

    for (const metal of metalValues) {
      for (const slide of this.getSlides(productInfo)) {
        if (this.slideMatchesMetal(slide, metal)) return slide;
      }
    }

    return null;
  },

  getFeaturedMediaId(productInfo, variant) {
    if (variant?.featured_media?.id) return variant.featured_media.id;

    if (!variant?.id) return null;

    const mapScript = productInfo.querySelector('[data-variant-featured-media]');
    if (!mapScript?.textContent?.trim()) return null;

    try {
      const map = JSON.parse(mapScript.textContent);
      return map[variant.id] ?? map[String(variant.id)] ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },

  findSlideByFeatured(productInfo, variant) {
    const gallery = this.getGallery(productInfo);
    if (!gallery || !variant) return null;

    const sectionId = productInfo.dataset.section;
    const featuredId = this.getFeaturedMediaId(productInfo, variant);

    if (!featuredId) return null;

    return gallery.querySelector(`[data-media-id="${sectionId}-${featuredId}"]`);
  },

  findSlide(productInfo, variant, metalValue = '') {
    return (
      this.findSlideByMetal(productInfo, variant, metalValue) ||
      this.findSlideByFeatured(productInfo, variant)
    );
  },

  preventStickyHeader() {
    document.querySelector('sticky-header')?.dispatchEvent(new Event('preventHeaderReveal'));
  },

  getHeaderOffset() {
    const header = document.querySelector('.section-header');
    return header ? header.getBoundingClientRect().bottom + 8 : 8;
  },

  scrollToSlide(slide) {
    if (!slide) return;

    const gallery = slide.closest('media-gallery');
    const layout = gallery?.dataset.desktopLayout || '';

    this.preventStickyHeader();

    if (!this.isDesktop()) {
      slide.parentElement?.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      return;
    }

    if (layout === 'stacked') {
      const viewer = slide.closest('[id^="GalleryViewer"]');
      const headerOffset = this.getHeaderOffset();
      const slideRect = slide.getBoundingClientRect();
      const viewerRect = viewer?.getBoundingClientRect();

      let scrollDelta = 0;

      if (viewerRect) {
        scrollDelta = slideRect.top - Math.max(viewerRect.top, headerOffset);
      } else {
        scrollDelta = slideRect.top - headerOffset;
      }

      if (Math.abs(scrollDelta) > 2) {
        window.scrollBy({ top: scrollDelta, behavior: 'smooth' });
      }

      return;
    }

    slide.parentElement?.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  },

  scrollToMetal(productInfo, variant, metalValue = '') {
    const slide = this.findSlide(productInfo, variant, metalValue);
    if (!slide) return;

    const run = () => this.scrollToSlide(slide);
    requestAnimationFrame(() => requestAnimationFrame(run));
    window.setTimeout(run, 120);
    window.setTimeout(run, 350);
  },

  handleMetalChange(productInfo, html, variant, metalValue = '') {
    const metal = metalValue || this.getSelectedMetal(productInfo);
    this.scrollToMetal(productInfo, variant, metal);
  },

  onMetalInputChange(event) {
    const input = event.target;
    if (input.type !== 'radio' || !input.checked) return;

    const fieldset = input.closest('.product-form__input--metal-swatch');
    if (!fieldset) return;

    const productInfo = input.closest('product-info');
    if (!productInfo) return;

    this.lastMetalClickAt = Date.now();
    this.scrollToMetal(productInfo, null, input.value);
  },

  onDocumentChange(event) {
    this.onMetalInputChange(event);
  },

  init() {
    document.addEventListener('change', this.onDocumentChange.bind(this), true);

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, variant } }) => {
        if (Date.now() - this.lastMetalClickAt > 2500) return;

        const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
        if (!productInfo) return;

        const metalValue = this.getSelectedMetal(productInfo);
        if (!metalValue) return;

        this.scrollToMetal(productInfo, variant, metalValue);
      });
    }
  },
};

window.ProductMetalGallery = ProductMetalGallery;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ProductMetalGallery.init());
} else {
  ProductMetalGallery.init();
}
